import React, { useEffect, useRef, useState } from "react";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  IconButton,
  styled,
  Autocomplete,
} from "@mui/material";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import FancyDialogActions from "./FancyDialogActions";
import FancyTooltip from "./FancyTooltip";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { getRequestProductsPerProject } from "../../services/exitTicket/ExitTicketService";
import { useApp } from "../../hooks/useApp";
import { CONSTANT } from "../../utils/Constants";
import Swal from "sweetalert2";

interface productsTypes {
  id?: number;
  description: string;
  quantity: number;
  availableQuantity: number;
  cost?: number;
}

interface AddOrUpdateExitTicketTypes {
  date: string;
  person: {
    id?: number;
    name: string;
  };
  personDeliveredFrom: {
    id?: number;
    name: string;
  };
  project: {
    id?: number;
    name: string;
  };
  projectTransfer: {
    id?: number;
    name: string;
  };
  notes: string;
  ticketNumber: string;
  status: {
    id?: number;
    name: string;
  };
  items: {
    productCost: number;
    product: productsTypes;
    quantity: number;
  }[];
  movement_type: {
    id: number;
    name: string;
  };
}

interface DialogProps {
  actionButton?: any;
  data: any;
  onSubmit: any;
  cancelModal: any;
  modalType: string;
  roleData?: any;
  handleActiveUser?: any;
  refreshData?: any;
  typeMovement?: any;
  projects?: any;
  products?: any;
  user?: any;
  projectManagersWithAttendants?: any;
}

const Actions = styled(DialogActions)(({ theme }) => ({
  borderTop: "1px solid #dee2e6",
}));

const AddOrUpdateExitTicketModal = ({
  actionButton,
  data,
  roleData,
  onSubmit,
  cancelModal,
  modalType,
  refreshData,
  typeMovement,
  projects,
  products,
  user,
  projectManagersWithAttendants,
}: DialogProps) => {
  const { setErrorMsg } = useApp();
  const validation = {
    id: Yup.number().required("Campo es requerido"),
    name: Yup.string().required("Campo es requerido"),
  };

  const validationSchema = Yup.object().shape({
    date: Yup.string().required("Campo requerido"),
    person: Yup.object()
      .required("Campo es requerido")
      .shape(validation)
      .typeError("Campo es requerido"),
    personDeliveredFrom: Yup.mixed().when('movement_type', {
      is: (val: any) => val?.name === 'Cambio de Proyecto',
      then: () => Yup.object()
        .required("Campo es requerido")
        .shape(validation)
        .typeError("Campo es requerido"),
      otherwise: () => Yup.mixed().nullable().transform((val) => (Array.isArray(val) && val.length === 0 ? null : val)).notRequired(),
    }),
    project: Yup.object()
      .required("Campo es requerido")
      .shape(validation)
      .typeError("Campo es requerido"),
    movement_type: Yup.object()
      .required("Campo es requerido")
      .shape(validation)
      .typeError("Campo es requerido"),
    status: Yup.object()
      .required("Campo es requerido")
      .shape(validation)
      .typeError("Campo es requerido"),
    items: Yup.array().of(
      Yup.object().shape({
        product: Yup.object()
          .shape({
            id: Yup.number().required("Campo es requerido"),
            description: Yup.string().required("Campo es requerido"),
          })
          .required("Campo es requerido"),
        quantity: Yup.number()
          .typeError("Campo es requerido")
          .required("Campo es requerido")
          .moreThan(0, "Debe ser mayor a 0"),
      })
    ),
  });

  console.log(data)

  const defaultValues = {
    id: data?.id ?? 0,
    date: data?.date ?? new Date().toISOString().split("T")[0],
    // "Se entrega a" siempre mapea person del backend (se muestra en la tabla)
    person: data?.person
      ? user?.find((f: any) => f.id === data?.person.id) ?? []
      : [],
    // "Persona que entrega" mapea deliveredToPerson del backend (para "Cambio de Proyecto")
    // Se busca en projectManagersWithAttendants
    personDeliveredFrom: data?.deliveredToPerson
      ? projectManagersWithAttendants?.find((f: any) => f.id === data?.deliveredToPerson?.id) ?? []
      : [],
    project: data?.project
      ? projects?.find((f: any) => f.id === data?.project.id) ?? []
      : [],
    projectTransfer: data?.ticketProjectTransfer
      ? projects?.find(
        (f: any) => f.id === data?.ticketProjectTransfer?.projectId
      ) ?? []
      : [],
    notes: data?.notes ?? "",
    status:
      data?.status === false
        ? { id: 0, name: "Pendiente" }
        : data?.status === true
          ? { id: 1, name: "Finalizada" }
          : {},
    ticketNumber: data?.ticketNumber ?? "",
    items: data?.ticketActivities?.length
      ? data.ticketActivities.map((ticket: any) => {
          // Si la boleta está finalizada, usar availableQuantity del backend (dato histórico)
          // Si está pendiente, buscar en products para obtener availableQuantity actual
          let availableQuantity = 0;
          
          if (data?.status === true) {
            // Boleta finalizada: usar el dato histórico del backend
            availableQuantity = ticket.availableQuantity ?? 0;
          } else {
            // Boleta pendiente: buscar en products el dato actual
            const fullProduct = products?.find((p: any) => p.id === ticket.product?.id);
            availableQuantity = fullProduct?.availableQuantity ?? 0;
          }
          
          return {
            productCost: ticket.productCost ?? 0,
            product: { 
              ...ticket.product, 
              availableQuantity: availableQuantity,
              cost: ticket.productCost ?? 0
            },
            quantity: ticket.quantity ?? 0,
          };
        })
      : null,
    movement_type: data?.activityType
      ? typeMovement?.find((f: any) => f.id === data?.activityType.id) ?? []
      : { id: 0, name: "" },
  };

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    getValues,
  } = useForm<AddOrUpdateExitTicketTypes>({
    defaultValues,
    //@ts-ignore
    resolver: yupResolver(validationSchema),
    mode: "onChange",
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "items",
  });

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const addItem = () => {
    append({
      product: { id: 0, description: "", quantity: 0, availableQuantity: 0 },
      quantity: 0,
      productCost: 0,
    });

    setTimeout(() => {
      const lastIndex = fields.length; // Último índice después de agregar
      inputRefs.current[lastIndex]?.focus(); // Enfocar en el nuevo insumo
    }, 100);
  };

  const project = watch("project");
  const projectTransfer = watch("projectTransfer");
  const movement_type = watch("movement_type");
  const products_watch = watch("items");
  const [previousMovementType, setPreviousMovementType] = useState<
    string | null
  >(movement_type?.name || null);

  const [selectedProducts, setSelectedProducts] = useState<any>(
    data?.ticketActivities?.map((ticket: any) => ticket.product) ?? []
  );

  const [inputs, setInputs] = useState([{ name: "", quantity: "" }]);
  const [inputValues, setInputValues] = useState<Record<number, string>>({});
  const [validationTimeouts, setValidationTimeouts] = useState<Record<number, NodeJS.Timeout>>({});
  const [projectProducts, setProjectProducts] = useState<any[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);

  const handleProductChange = (index: any, newValue: any) => {
    const newSelectedProducts: any = [...selectedProducts];
    newSelectedProducts[index] = newValue;
    setSelectedProducts(newSelectedProducts);
    setValue(`items.${index}.quantity`, 0);
  };

  const handleRemoveItem = (index: number) => {
    setSelectedProducts((prev: any) => {
      const updatedProducts = [...prev];
      updatedProducts.splice(index, 1);
      return updatedProducts;
    });

    // Limpiar el valor del input para este índice
    setInputValues((prev) => {
      const updated = { ...prev };
      delete updated[index];
      // Reajustar los índices
      const newInputValues: Record<number, string> = {};
      Object.keys(updated).forEach((key) => {
        const numKey = parseInt(key);
        if (numKey > index) {
          newInputValues[numKey - 1] = updated[numKey];
        } else {
          newInputValues[numKey] = updated[numKey];
        }
      });
      return newInputValues;
    });

    // Limpiar timeouts de validación
    setValidationTimeouts((prev) => {
      const updated = { ...prev };
      if (updated[index]) {
        clearTimeout(updated[index]);
        delete updated[index];
      }
      // Reajustar los índices
      const newTimeouts: Record<number, NodeJS.Timeout> = {};
      Object.keys(updated).forEach((key) => {
        const numKey = parseInt(key);
        if (numKey > index) {
          newTimeouts[numKey - 1] = updated[numKey];
        } else {
          newTimeouts[numKey] = updated[numKey];
        }
      });
      return newTimeouts;
    });

    remove(index);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      addItem();
    }
  };

  const isBodegaProject = (projectId?: number | null) =>
    projectId === CONSTANT.BODEGA_PROJECT_ID;

  // Retorno a Bodega con proyecto = BODEGA: la operación SUMA al inventario general,
  // no resta. No hay tope de disponibilidad ni validación contra availableQuantity.
  const isBodegaReturn = (
    movementName?: string | null,
    projectId?: number | null
  ) => movementName === "Retorno a Bodega" && isBodegaProject(projectId);

  const handlePreSubmit = async (formData: AddOrUpdateExitTicketTypes) => {
    const issues = isBodegaReturn(
      formData.movement_type?.name,
      formData.project?.id
    )
      ? []
      : formData.items.filter(
          (item: any) =>
            item.product?.id &&
            item.product.id > 0 &&
            (item.product.availableQuantity === 0 ||
              item.quantity > item.product.availableQuantity)
        );

    if (issues.length > 0) {
      const itemList = issues
        .map(
          (item: any) =>
            `<b>${item.product.code ? item.product.code + " - " : ""}${item.product.description}</b>: Disponible ${item.product.availableQuantity}, Solicitado ${item.quantity}`
        )
        .join("<br>");

      const result = await Swal.fire({
        title: "Atención: disponibilidad insuficiente",
        html: `Los siguientes insumos tienen disponibilidad insuficiente:<br><br>${itemList}`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Continuar de todos modos",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#1976d2",
        cancelButtonColor: "#CC0000",
      });

      if (!result.isConfirmed) return;
    }

    onSubmit(formData);
  };

  const usesProjectSupplies = (name?: string | null) =>
    name === "Cambio de Proyecto" || name === "Retorno a Bodega";

  useEffect(() => {
    const dataInit = async () => {
      try {
        // Caso especial: BODEGA (212) comparte el inventario general. No cargamos supplies del proyecto;
        // el Autocomplete usa la lista de inventario general (`products`), igual que en Salida de Bodega.
        if (
          usesProjectSupplies(movement_type?.name) &&
          isBodegaProject(project?.id)
        ) {
          setProjectProducts([]);
          if (isInitialLoad) {
            setIsInitialLoad(false);
          }
        }
        // Caso: Cambio de project en tipo que usa supplies del proyecto (limpia productos y carga nuevos)
        else if (
          usesProjectSupplies(movement_type?.name) &&
          project?.id
        ) {
          if (isInitialLoad) {
            const loadedProducts = await handleFetchData();
            setIsInitialLoad(false);

            // En edición de boleta pendiente, actualizar availableQuantity con datos del proyecto de origen
            if (modalType === "update") {
              const currentItems = getValues("items");
              if (currentItems?.length) {
                const updatedItems = currentItems.map((item: any) => {
                  const projectProduct = loadedProducts.find((p: any) => p.id === item.product?.id);
                  return {
                    ...item,
                    product: {
                      ...item.product,
                      availableQuantity: projectProduct?.availableQuantity ?? 0,
                    },
                  };
                });
                setValue("items", updatedItems);
              }
            }
          } else {
            setValue("items", []);
            setSelectedProducts([]);
            await handleFetchData();
          }
        }

        // Caso: Cuando el tipo de movimiento usa supplies del proyecto y se ha cambiado de tipo
        else if (
          modalType === "update" &&
          usesProjectSupplies(movement_type?.name) &&
          !usesProjectSupplies(previousMovementType) &&
          project?.id
        ) {
          await handleFetchData();
        }

        // Caso: Creación con tipo que usa supplies del proyecto
        else if (
          modalType === "create" &&
          usesProjectSupplies(movement_type?.name) &&
          project?.id
        ) {
          await handleFetchData();
        }

        // Caso: Cambio desde un tipo que usa supplies del proyecto a otro que no
        else if (
          usesProjectSupplies(previousMovementType) &&
          !usesProjectSupplies(movement_type?.name)
        ) {
          setValue("items", []);
          setSelectedProducts([]);
          setValue("projectTransfer", { id: 0, name: "" });
        }

        // Actualiza el tipo de movimiento previo al final, solo cuando ambos valores han cambiado
        if (movement_type?.name !== previousMovementType) {
          setPreviousMovementType(movement_type?.name || null);
        }
      } catch (error) {
        console.error("Error en dataInit:", error);
      }
    };

    if (project?.id && movement_type?.name) {
      dataInit();
    }

    // eslint-disable-next-line
  }, [project, movement_type, modalType]);

  // Limpiar timeouts cuando el componente se desmonte
  useEffect(() => {
    return () => {
      Object.values(validationTimeouts).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, [validationTimeouts]);

  const handleFetchData = async (): Promise<any[]> => {
    try {
      const responseData: any = await getRequestProductsPerProject(
        project?.id ?? 0
      );

      if (responseData) {
        const transformedSelectData = responseData.map((item: any) => ({
          id: item.productId,
          description: item.productName,
          code: item.productCode,
          availableQuantity: item.totalQuantity,
          cost: item.productCost,
        }));

        setProjectProducts(transformedSelectData);
        return transformedSelectData;
      }
      return [];
    } catch (error: any) {
      setErrorMsg && setErrorMsg(error.message);
      return [];
    }
  };

  /* const handleValidate = async () => { 
    products_watch.map((item1: any, index: number) => {
      let count = 0;
      console.log("count: ", count);
      const item2 = products.find(
        (item2: any) => item1.product.id === item2.id
      );
      if (item2 && item1.quantity > item2.quantity) {
        count++;
        setError(`items.${index}.quantity`, {
          type: "custom",
          message: "Valor no puede ser mayor al disponibleeee",
        });
        
          //error: `Quantity in array1 (${item1.quantity}) exceeds quantity in array2 (${item2.quantity})`
        
      }
      if (count > 0) {
        console.log("se ejecuta handleSubmit(onSubmit)");
        //handleSubmit(onSubmit)
      }

      //return item1;
    });
  }; */

  return (
    <>
      <DialogTitle id="alert-dialog-title" sx={{ pt: 4, px: 4 }}>
        {modalType === "create"
          ? "Crear Boleta de Salida"
          : "Actualizar Boleta de Salida"}
      </DialogTitle>

      <DialogContent sx={{ px: 4, pb: 0 }}>
        <form>
          <Grid container spacing={3} maxWidth="lg" sx={{ pt: 4, pb: 8 }}>
            <Grid item xs={modalType === "create" ? 6 : 4}>
              <Controller
                name="date"
                control={control}
                render={({ field }) => (
                  <TextField
                    fullWidth
                    label="Fecha"
                    type="date"
                    disabled={data?.status === true}
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(errors.date)}
                    helperText={errors.date?.message}
                    {...field}
                  />
                )}
              />
            </Grid>
            {modalType === "update" && (
              <Grid item xs={4}>
                <Controller
                  name="ticketNumber"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      label="N° Boleta"
                      disabled={data?.status === true}
                      type="text"
                      InputProps={{ readOnly: true }}
                      {...field}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
            )}
            <Grid item xs={modalType === "create" ? 6 : 4}>
              <Controller
                name={"status"}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    onChange={(event, newValue: any) => {
                      console.log(newValue);
                      onChange(newValue || null);
                    }}
                    options={[
                      { id: 0, name: "Pendiente" },
                      { id: 1, name: "Finalizado" },
                    ]}
                    getOptionLabel={(option: any) => option.name || ""}
                    isOptionEqualToValue={(option: any, value: any) =>
                      option.id === value.id
                    }
                    disabled={data?.status === true}
                    value={value}
                    renderInput={(params) => {
                      const inputProps = params.inputProps;
                      inputProps.autoComplete = "new-password";
                      return (
                        <TextField
                          {...params}
                          fullWidth
                          label="Estado"
                          variant="outlined"
                          sx={{ "& input": { pl: "0!important" } }}
                          error={!!errors.status}
                          helperText={
                            (errors.status && errors.status.name?.message) ||
                            errors.status?.message
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      );
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={modalType === "create" ? 6 : 4}>
              <Controller
                name={"person"}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    onChange={(event, newValue: any) => {
                      onChange(newValue);
                    }}
                    options={user || []}
                    getOptionLabel={(option: any) =>
                      `${option.name || ""} ${option.lastName || ""} ${option.surname || ""
                        }`.trim()
                    }
                    isOptionEqualToValue={(option: any, value: any) =>
                      option.id === value?.id
                    }
                    disabled={data?.status === true}
                    value={value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        label="Se entrega a"
                        variant="outlined"
                        sx={{ "& input": { pl: "0!important" } }}
                        error={!!errors.person}
                        helperText={errors.person && errors.person.message}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            {movement_type?.name === "Cambio de Proyecto" && (
              <Grid item xs={modalType === "create" ? 6 : 4}>
                <Controller
                  name={"personDeliveredFrom"}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Autocomplete
                      onChange={(event, newValue: any) => {
                        onChange(newValue);
                      }}
                      options={projectManagersWithAttendants || []}
                      getOptionLabel={(option: any) =>
                        `${option.name || ""} ${option.lastName || ""} ${option.surname || ""
                          }`.trim()
                      }
                      isOptionEqualToValue={(option: any, value: any) =>
                        option.id === value?.id
                      }
                      disabled={data?.status === true}
                      value={value}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          label="Persona que entrega"
                          variant="outlined"
                          sx={{ "& input": { pl: "0!important" } }}
                          error={!!errors.personDeliveredFrom}
                          helperText={errors.personDeliveredFrom && (errors.personDeliveredFrom as any).message}
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  )}
                />
              </Grid>
            )}

            <Grid item xs={modalType === "create" ? 6 : 4}>
              <Controller
                name={"project"}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    onChange={(event, newValue: any) => {
                      onChange(newValue);
                    }}
                    options={projects !== null ? projects : {}}
                    getOptionLabel={(option: any) => option.name || ""}
                    isOptionEqualToValue={(option: any, value: any) =>
                      option.id === value.id
                    }
                    disabled={data?.status === true}
                    value={value}
                    renderInput={(params) => {
                      const inputProps = params.inputProps;
                      inputProps.autoComplete = "new-password";
                      return (
                        <TextField
                          {...params}
                          fullWidth
                          label={
                            movement_type?.name === "Cambio de Proyecto"
                              ? "Proyecto origen"
                              : "Proyecto"
                          }
                          variant="outlined"
                          sx={{ "& input": { pl: "0!important" } }}
                          error={!!errors.project}
                          helperText={errors.project && errors.project.message}
                          InputLabelProps={{ shrink: true }}
                        />
                      );
                    }}
                  />
                )}
              />
            </Grid>

            {movement_type?.name === "Cambio de Proyecto" && (
              <Grid item xs={modalType === "create" ? 6 : 4}>
                <Controller
                  name={"projectTransfer"}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Autocomplete
                      onChange={(event, newValue: any) => {
                        onChange(newValue);
                      }}
                      options={projects !== null ? projects : {}}
                      getOptionLabel={(option: any) => option.name || ""}
                      isOptionEqualToValue={(option: any, value: any) =>
                        option.id === value.id
                      }
                      disabled={data?.status === true}
                      value={value}
                      renderInput={(params) => {
                        const inputProps = params.inputProps;
                        inputProps.autoComplete = "new-password";
                        return (
                          <TextField
                            {...params}
                            fullWidth
                            label="Proyecto destino"
                            variant="outlined"
                            sx={{ "& input": { pl: "0!important" } }}
                            error={!!errors.project}
                            helperText={
                              errors.project && errors.project.message
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        );
                      }}
                    />
                  )}
                />
              </Grid>
            )}

            <Grid item xs={modalType === "create" ? 6 : 4}>
              <Controller
                name={"movement_type"}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    onChange={(event, newValue: any) => {
                      console.log("newValue: ", newValue);
                       if (newValue?.name === "Cambio de Proyecto" || newValue === null) {
                            setValue("items", []); // Clear the list of items
                            setSelectedProducts([]); // Clear selected products
                            setValue("projectTransfer", { id: 0, name: "" }); // Reset project transfer
                        } else {
                          setProjectProducts([]); // Clear project products when not "Cambio de Proyecto"
                        }
                      onChange(newValue);
                    }}
                    options={typeMovement !== null ? typeMovement : {}}
                    getOptionLabel={(option: any) => option.name || ""}
                    isOptionEqualToValue={(option: any, value: any) =>
                      option.id === value.id
                    }
                    disabled={data?.status === true}
                    value={value}
                    renderInput={(params) => {
                      const inputProps = params.inputProps;
                      inputProps.autoComplete = "new-password";
                      return (
                        <TextField
                          {...params}
                          fullWidth
                          label="Tipo de movimiento"
                          variant="outlined"
                          sx={{ "& input": { pl: "0!important" } }}
                          error={!!errors.movement_type}
                          helperText={
                            (errors.movement_type &&
                              errors.movement_type.name?.message) ||
                            errors.movement_type?.message
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      );
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Grid container spacing={2} /* alignItems="center" */>
                <Grid item xs={12}>
                  <strong>Insumos / Herramientas</strong>
                </Grid>

                {fields.map((item, index: number) => (
                  <React.Fragment key={item.id}>
                    <Grid item xs={8}>
                      <Controller
                        name={`items.${index}.product`}
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <Autocomplete
                            onChange={(event, newValue) => {
                              // Verificar si el producto ya está seleccionado
                              const isAlreadySelected = selectedProducts.some(
                                (selected: any, selectedIndex: number) =>
                                  selected?.id === newValue?.id && selectedIndex !== index
                              );
                              
                              if (isAlreadySelected && newValue) {
                                // Mostrar SweetAlert
                                Swal.fire({
                                  icon: 'warning',
                                  title: 'Producto duplicado',
                                  text: 'Este producto ya ha sido agregado a la lista',
                                  confirmButtonText: 'Entendido'
                                });
                                return;
                              }
                              
                              // Limpiar error si existe
                              onChange(newValue);
                              handleProductChange(index, newValue);
                              if (newValue) {
                                setValue(`items.${index}.productCost`, newValue.cost || 0);
                              }
                            }}
                            onInputChange={(event, newInputValue) => {
                              // Actualizar el valor del input
                              setInputValues(prev => ({ ...prev, [index]: newInputValue }));
                              
                              // Limpiar el timeout anterior si existe
                              setValidationTimeouts(prev => {
                                if (prev[index]) {
                                  clearTimeout(prev[index]);
                                }
                                return { ...prev };
                              });
                              
                              // Verificar si el texto ingresado coincide con un producto ya seleccionado
                              if (newInputValue.trim()) {
                                // Crear un nuevo timeout para validar después de 800ms
                                const timeoutId = setTimeout(() => {
                                  console.log("Validando producto después del timeout:", newInputValue);
                                  console.log("Productos seleccionados:", selectedProducts);
                                  console.log("Índice actual:", index);
                                  
                                  // Buscar si el texto ingresado coincide con algún producto ya seleccionado
                                  const isMatchingSelectedProduct = selectedProducts.some((selected: any, selectedIndex: number) => {
                                    if (selectedIndex === index || !selected) return false;

                                    // Obtener el producto completo de la lista de productos
                                    const fullProduct = products?.find((p: any) => p.id === selected.id);
                                    if (!fullProduct) return false;

                                    const productCode = fullProduct.code || '';
                                    const productDescription = fullProduct.description || '';
                                    const fullProductText = `${productCode} - ${productDescription}`.toLowerCase();
                                    const inputText = newInputValue.toLowerCase();

                                    // Verificar si el input coincide con:
                                    // 1. El código completo
                                    // 2. La descripción completa o parte de ella
                                    // 3. El formato completo del producto
                                    const matches = productCode.toLowerCase().includes(inputText) ||
                                           productDescription.toLowerCase().includes(inputText) ||
                                           fullProductText.includes(inputText);
                                    return matches;
                                  });

                                  if (isMatchingSelectedProduct) {
                                    // Mostrar SweetAlert
                                    Swal.fire({
                                      icon: 'warning',
                                      title: 'Producto duplicado',
                                      text: 'Este producto ya ha sido agregado a la lista',
                                      confirmButtonText: 'Entendido'
                                    });
                                  }
                                }, 30000); 
                                
                                // Guardar el timeout
                                setValidationTimeouts(prev => ({
                                  ...prev,
                                  [index]: timeoutId
                                }));
                              } else {
                                console.log("Texto vacío, no validar");
                              }
                            }}
                            options={
                              !movement_type?.name || !project?.id
                                ? []
                                : usesProjectSupplies(movement_type?.name) &&
                                  !isBodegaProject(project?.id)
                                ? projectProducts.filter(
                                  (product: any) =>
                                    !selectedProducts.some(
                                      (selected: any, selectedIndex: number) =>
                                        selected?.id === product.id && selectedIndex !== index
                                    )
                                )
                                : products !== null
                                ? products.filter(
                                  (product: any) =>
                                    !selectedProducts.some(
                                      (selected: any, selectedIndex: number) =>
                                        selected?.id === product.id && selectedIndex !== index
                                    )
                                )
                                : []
                            }
                            getOptionLabel={(option: any) =>
                              option.code && option.description
                                ? `${option.code} - ${option.description} (Disponible: ${option.availableQuantity || 0})`
                                : ""
                            }
                            isOptionEqualToValue={(option: any, value: any) =>
                              option.id === value.id
                            }
                            disabled={data?.status === true}
                            value={value}
                            renderInput={(params) => {
                              const inputProps = params.inputProps;
                              inputProps.autoComplete = "new-password";
                              
                              // Verificar si hay error por producto
                              const hasProductError = Boolean(
                                errors.items?.[index]?.product
                              );
                              
                              return (
                                <TextField
                                  {...params}
                                  fullWidth
                                  label="Insumo / Herramienta"
                                  variant="outlined"
                                  sx={{ "& input": { pl: "0!important" } }}
                                  error={hasProductError}
                                  helperText={errors.items?.[index]?.product?.message}
                                  InputLabelProps={{ shrink: true }}
                                  inputRef={(el) => (inputRefs.current[index] = el)}
                                />
                              );
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={4}
                      sx={{ display: "flex", alignItems: "flex-start" }}
                    >
                      <Controller
                        name={`items.${index}.quantity`}
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <TextField
                            fullWidth
                            size="small"
                            label={"Cantidad"}
                            disabled={data?.status === true}
                            type="number"
                            variant="outlined"
                            onKeyDown={(e: any) => handleKeyDown(e)}
                            onWheel={(e) => (e.target as HTMLInputElement).blur()}
                            value={value}
                            {...register(`items.${index}.quantity` as const, {
                              validate: {
                                greaterThanZero: (v) =>
                                  Number(v) > 0 || "Debe ser mayor a 0",
                              },
                            })}
                            error={Boolean(errors.items?.[index]?.quantity)}
                            helperText={
                              errors.items?.[index]?.quantity?.message
                            }
                            inputProps={{
                              min: 0,
                              max: isBodegaReturn(movement_type?.name, project?.id)
                                ? undefined
                                : movement_type?.name === "Salida de Bodega" ||
                                  movement_type?.name === "Salida a Taller Servicio" ||
                                  isBodegaProject(project?.id)
                                  ? products_watch[index].product?.availableQuantity
                                  : products_watch[index].product?.quantity,
                            }}
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      />

                      <IconButton
                        color="error"
                        onClick={() => handleRemoveItem(index)}
                        disabled={data?.status === true}
                      >
                        <RemoveCircleIcon />
                      </IconButton>
                    </Grid>
                  </React.Fragment>
                ))}

                <Grid item xs={2}>
                  <FancyTooltip title={"Agregar insumo"} placement="top">
                    <IconButton
                      color="primary"
                      onClick={addItem}
                      disabled={data?.status === true || !movement_type?.name || !project?.id}
                    >
                      <AddCircleIcon />
                    </IconButton>
                  </FancyTooltip>
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name={"notes"}
                    control={control}
                    render={({ field: { value } }) => (
                      <TextField
                        fullWidth
                        size="small"
                        label="Notas"
                        multiline
                        maxRows={4}
                        disabled={data?.status === true}
                        value={value}
                        variant="outlined"
                        {...register("notes")}
                        sx={{ background: "#FFF" }}
                        error={errors.notes && Boolean(errors.notes)}
                        helperText={errors.notes && errors.notes.message}
                        inputProps={{
                          autoComplete: "new-password",
                          form: {
                            autoComplete: "off",
                          },
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </DialogContent>

      <FancyDialogActions
        handleAccept={handleSubmit(handlePreSubmit)}
        cancelModal={cancelModal}
      />
    </>
  );
};

export default AddOrUpdateExitTicketModal;
